import React, { useState, useEffect, useRef } from "react";
import { Modal, Button } from "react-bootstrap";
import { TransitionGroup, CSSTransition } from "react-transition-group";
import { PaginationControls } from "../../../../hooks/customHook/paginationHook/usePagination";
import { useServerPagination } from "../../../../hooks/customHook/paginationHook/useServerPagination";
import {
  formatToTwoDecimal,
  parseNumber,
} from "../../../../utils/numberFormatter";
import axios from "axios";
import BASE_URL from "../../../../assets/global/url";
import swal from "sweetalert";

const BackloadModal = ({
  show,
  onHide,
  invoiceId,
  transactionId,
  userLoggedID,
}) => {
  const backloadPagination = useServerPagination(
    `${BASE_URL}/invoice/backload/items-to-return`,
    10,
  );
  const [itemsToReturn, setItemsToReturn] = useState([]);
  const [selectedItems, setSelectedItems] = useState(new Set());

  useEffect(() => {
    if (show && invoiceId && transactionId) {
      getItemsToReturn(invoiceId, transactionId);
    }
  }, [show, invoiceId, transactionId]);

  useEffect(() => {
    if (backloadPagination.data) {
      console.log(`backloadPagination.data`, backloadPagination.data);
      setItemsToReturn(
        backloadPagination.data.map((item) => {
          const salesInvoiceId = item.sales_invoice_id;
          const salesTransactionNumber = item.sales_invoice.transaction_id;
          const productId =
            item.sales_approved_inventories[0]?.stock_management?.product_list
              ?.product_id;
          const productName =
            item.sales_approved_inventories[0]?.stock_management?.product_list
              ?.product_name;
          const unitPrice = item.unit_price;
          const originalINVUnitPrice =
            item.sales_approved_inventories[0]?.stock_management.price;
          const netWeight = item.net_weight;
          const quantity = item.quantity;
          const moisture = item.moisture || 0;
          const staticNetWeight = item.static_net_weight;
          const originalQuantity = item.sales_approved_inventories.reduce(
            (sum, sai) => sum + (sai.quantity || 0),
            0,
          );

          return {
            salesInvoiceId,
            salesTransactionNumber,
            productId,
            productName,
            netWeight: netWeight,
            quantity: quantity,
            quantityToReturn: formatToTwoDecimal(quantity),
            unitPrice,
            originalINVUnitPrice,
            moisture,
            static_netWeight: staticNetWeight,
            originalQuantity,
          };
        }),
      );
      setSelectedItems(new Set());
    }
  }, [backloadPagination.data]);

  // Quantity = Quantity - Return
  // Net Weight = proportional to quantity reduction (moisture already baked in)
  const calculateValues = (
    originalQuantity,
    returnQuantity,
    originalNetWeight,
  ) => {
    const newQuantity = originalQuantity - returnQuantity;
    const newNetWeight =
      originalQuantity > 0
        ? (newQuantity / originalQuantity) * originalNetWeight
        : 0;

    return {
      newQuantity,
      newNetWeight,
    };
  };

  const calculateTotalReturned = (originalQuantity, currentQuantity) => {
    return originalQuantity - currentQuantity;
  };

  // Original Weight = static_net_weight ÷ (1 − Moisture)
  // Total Returned = Original Weight - Current Quantity
  // const calculateTotalReturned = (
  //   staticNetWeight,
  //   currentQuantity,
  //   moisture,
  // ) => {
  //   const moistureDecimal = moisture / 100; // Convert percentage to decimal
  //   const originalWeight = staticNetWeight / (1 - moistureDecimal);
  //   const totalReturned = originalWeight - currentQuantity;
  //   return totalReturned;
  // };
  const getItemsToReturn = (salesInvoiceId, salesTransactionNumber) => {
    try {
      backloadPagination.updateParams({
        salesInvoiceId,
        salesTransactionNumber,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleReturnProduct = async (product) => {
    try {
      const returnQuantity = parseFloat(
        String(product.quantityToReturn).replace(/,/g, ""),
      );

      const { newQuantity, newNetWeight } = calculateValues(
        product.quantity,
        returnQuantity,
        product.netWeight,
      );

      const productWithCalculatedValues = {
        ...product,
        quantityToReturn: returnQuantity,
        calculatedQuantity: newQuantity,
        calculatedNetWeight: newNetWeight,
        unitPrice: product.unitPrice,
        originalINVUnitPrice: product.originalINVUnitPrice,
      };

      const res = await axios.post(
        `${BASE_URL}/invoice/backload/return-product`,
        {
          product: productWithCalculatedValues,
          userLoggedID,
        },
      );
      if (res.status === 200) {
        swal({
          icon: "success",
          title: "Return Successful",
          text: `Product has been returned.`,
          timer: 3000,
          buttons: false,
        }).then(() => {
          getItemsToReturn(
            product.salesInvoiceId,
            product.salesTransactionNumber,
          );
        });
      }
    } catch (error) {
      if (error.response && error.response.status === 400) {
        swal({
          icon: "error",
          title: error.response.data.title,
          text: error.response.data.message,
        });
        return;
      }

      if (error.response && error.response.status === 409) {
        const soldQuantity = error.response.data.soldQuantity;
        const wrapper = document.createElement("div");
        wrapper.classList.add("center-swal-text");
        wrapper.innerHTML = `The return quantity exceeds the sold quantity <strong>(${formatToTwoDecimal(soldQuantity)})</strong>`;
        swal({
          icon: "error",
          title: "Invalid Quantity To Return",
          content: wrapper,
        });
      }

      console.error(error);
    }
  };

  const handleBulkReturn = () => {
    if (selectedItems.size === 0) {
      swal({
        icon: "warning",
        title: "No Items Selected",
        text: "Please select at least one item to return.",
      });
      return;
    }

    const itemsWithValidQuantity = Array.from(selectedItems).filter((index) => {
      const item = itemsToReturn[index];
      return parseNumber(item.quantityToReturn) > 0;
    });

    if (itemsWithValidQuantity.length === 0) {
      swal({
        icon: "warning",
        title: "Invalid Quantities",
        text: "All selected items must have a return quantity greater than zero.",
      });
      return;
    }

    swal({
      icon: "warning",
      title: "Confirm Product Return",
      text: `Are you sure you want to return ${itemsWithValidQuantity.length} product(s)? This action cannot be undone.`,
      buttons: ["Cancel", "Yes, Return"],
      dangerMode: true,
    }).then(async (confirm) => {
      if (confirm) {
        for (const index of itemsWithValidQuantity) {
          // console.log(itemsToReturn[index]);
          // return;
          await handleReturnProduct(itemsToReturn[index]);
        }
      }
    });
  };

  const toggleItemSelection = (index) => {
    const newSelectedItems = new Set(selectedItems);
    if (newSelectedItems.has(index)) {
      newSelectedItems.delete(index);
    } else {
      newSelectedItems.add(index);
    }
    setSelectedItems(newSelectedItems);
  };

  const handleClose = () => {
    setItemsToReturn([]);
    setSelectedItems(new Set());
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title className="text-primary fw-bold">Backload</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="overflow-auto">
          <div
            style={{ maxHeight: "80vh", overscrollBehaviorBlock: "contain" }}
            className="overflow-auto"
          >
            <table className="table table-bordered table-striped table-responsive text-center text-nowrap">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedItems(
                            new Set(itemsToReturn.map((_, index) => index)),
                          );
                        } else {
                          setSelectedItems(new Set());
                        }
                      }}
                      checked={
                        selectedItems.size === itemsToReturn.length &&
                        itemsToReturn.length > 0
                      }
                    />
                  </th>
                  <th>Product Name</th>
                  <th>Quantity</th>
                  <th>Moisture (%)</th>
                  <th>Net Weight</th>
                  <th>Quantity to Return</th>
                  <th>Total Returned</th>
                </tr>
              </thead>
              <tbody>
                <TransitionGroup component={null}>
                  {itemsToReturn.map((item, index) => {
                    const returnQuantity = parseFloat(
                      String(item.quantityToReturn || 0).replace(/,/g, ""),
                    );

                    const { newQuantity, newNetWeight } =
                      returnQuantity > 0
                        ? calculateValues(
                            item.quantity,
                            returnQuantity,
                            item.netWeight,
                          )
                        : {
                            newQuantity: item.quantity,
                            newNetWeight: item.netWeight,
                          };

                    const totalReturned = calculateTotalReturned(
                      item.originalQuantity,
                      item.quantity,
                    );

                    return (
                      <CSSTransition
                        key={index}
                        timeout={300}
                        classNames="table-row"
                      >
                        <tr key={index}>
                          <td className="align-middle">
                            <input
                              type="checkbox"
                              checked={selectedItems.has(index)}
                              onChange={() => toggleItemSelection(index)}
                            />
                          </td>
                          <td className="align-middle">{item.productName}</td>
                          <td className="align-middle">
                            {formatToTwoDecimal(item.quantity)}
                          </td>
                          <td className="align-middle">
                            {formatToTwoDecimal(item.moisture)}%
                          </td>
                          <td className="align-middle">
                            {formatToTwoDecimal(item.netWeight)}
                          </td>
                          <td className="align-middle">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="0.00"
                              value={item.quantityToReturn}
                              readOnly
                            />
                          </td>
                          <td className="align-middle bg-light">
                            {formatToTwoDecimal(totalReturned)}
                          </td>
                        </tr>
                      </CSSTransition>
                    );
                  })}
                </TransitionGroup>
              </tbody>
            </table>
          </div>
          <div className="d-flex justify-content-end">
            <Button
              variant="primary"
              onClick={handleBulkReturn}
              disabled={selectedItems.size === 0}
            >
              Return Selected Items ({selectedItems.size})
            </Button>
          </div>
          <div className="pb-2">
            <PaginationControls {...backloadPagination} />
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default BackloadModal;
